import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ExternalLink, Loader2, Building2 } from "lucide-react";

/**
 * AdminTemples — review queue for Temple business doc verifications.
 *
 * Backend:
 *   GET  /api/admin/temple-verifications?status=pending|approved|rejected|all
 *   POST /api/admin/temple-verifications/{userId}/decision {decision, note?}
 */
export default function AdminTemples() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingOn, setActingOn] = useState(null); // userId currently being approved/rejected
  const [rejectNote, setRejectNote] = useState({}); // {userId: note}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/temple-verifications", {
        params: { status: statusFilter },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load temple queue");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [statusFilter]);

  const decide = async (uid, decision, note) => {
    setActingOn(uid);
    try {
      await api.post(`/admin/temple-verifications/${uid}/decision`, {
        decision, note: note || null,
      });
      toast.success(`Submission ${decision}d`);
      setRejectNote((s) => { const c = { ...s }; delete c[uid]; return c; });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="parchment p-6 space-y-5" data-testid="admin-temples">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-secondary" strokeWidth={1.75} />
            <span className="label-mystic text-secondary">Temple Verifications</span>
          </div>
          <h2 className="font-serif text-3xl mt-1">Review business docs</h2>
          <p className="text-sm text-foreground/65 mt-1 max-w-xl">
            Providers submit a business doc + legal entity name. Approve to
            grant the Temple tier criterion; reject with a note if the doc is
            unclear so they can re-upload.
          </p>
        </div>
        <div className="flex gap-1 rounded-sm border border-secondary/30 overflow-hidden text-[11px]">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 uppercase tracking-[0.18em] ${
                statusFilter === s
                  ? "bg-secondary text-secondary-foreground"
                  : "text-foreground/65 hover:bg-secondary/[0.08]"
              }`}
              data-testid={`admin-temples-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-xs text-foreground/60 flex items-center gap-2" data-testid="admin-temples-loading">
          <Loader2 size={12} className="animate-spin" /> Loading queue…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-sm border border-dashed border-secondary/20 p-6 text-center text-sm text-foreground/55" data-testid="admin-temples-empty">
          No {statusFilter === "all" ? "" : statusFilter + " "}submissions.
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const u = it.user || {};
          const isPending = it.templeVerifyStatus === "pending";
          const busy = actingOn === it.userId;
          const note = rejectNote[it.userId] || "";
          return (
            <div
              key={it.userId}
              className="rounded-sm border border-secondary/20 bg-secondary/[0.03] p-4"
              data-testid={`admin-temple-row-${it.userId}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <div className="label-mystic text-secondary/70 text-[10px]">Practitioner</div>
                  <div className="font-serif text-lg text-foreground" data-testid={`admin-temple-name-${it.userId}`}>
                    {u.name || "(unknown)"}
                  </div>
                  <div className="text-[11px] text-foreground/55 break-all">
                    {u.email} · {u.role}
                  </div>
                </div>
                <div>
                  <div className="label-mystic text-secondary/70 text-[10px]">Legal entity</div>
                  <div className="text-sm text-foreground" data-testid={`admin-temple-entity-${it.userId}`}>
                    {it.templeLegalEntityName}
                  </div>
                  <div className="text-[11px] text-foreground/55">
                    {it.templeEntityType}
                    {it.tradition ? ` · ${it.tradition}` : ""}
                  </div>
                </div>
                <div>
                  <div className="label-mystic text-secondary/70 text-[10px]">Status</div>
                  <StatusPill status={it.templeVerifyStatus} />
                  <div className="text-[11px] text-foreground/55 mt-1">
                    {it.templeVerifySubmittedAt
                      ? `Submitted ${formatDate(it.templeVerifySubmittedAt)}`
                      : null}
                  </div>
                  {it.templeVerifyRejectionNote && (
                    <div className="text-[11px] text-destructive mt-1" data-testid={`admin-temple-rejection-note-${it.userId}`}>
                      Reject note: {it.templeVerifyRejectionNote}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <a
                  href={it.templeBusinessDocUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-secondary hover:text-secondary/80"
                  data-testid={`admin-temple-doc-link-${it.userId}`}
                >
                  <ExternalLink size={11} /> Open business doc
                </a>
                {isPending && (
                  <>
                    <button
                      type="button"
                      onClick={() => decide(it.userId, "approve")}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-secondary text-secondary-foreground text-xs uppercase tracking-[0.18em] hover:bg-secondary/85 disabled:opacity-50"
                      data-testid={`admin-temple-approve-${it.userId}`}
                    >
                      {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                      Approve
                    </button>
                    <input
                      type="text"
                      value={note}
                      onChange={(e) => setRejectNote((s) => ({ ...s, [it.userId]: e.target.value }))}
                      placeholder="Rejection note (optional)"
                      className="flex-1 min-w-[200px] px-3 py-1.5 rounded-sm border border-destructive/30 bg-background text-foreground text-xs"
                      data-testid={`admin-temple-reject-note-${it.userId}`}
                    />
                    <button
                      type="button"
                      onClick={() => decide(it.userId, "reject", note)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-destructive/50 text-destructive text-xs uppercase tracking-[0.18em] hover:bg-destructive/10 disabled:opacity-50"
                      data-testid={`admin-temple-reject-${it.userId}`}
                    >
                      {busy ? <Loader2 size={11} className="animate-spin" /> : <XCircle size={11} />}
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pending", cls: "border-amber-500/40 text-amber-200 bg-amber-500/[0.06]" },
    approved: { label: "Approved", cls: "border-secondary/40 text-secondary bg-secondary/[0.06]" },
    rejected: { label: "Rejected", cls: "border-destructive/40 text-destructive bg-destructive/[0.06]" },
  };
  const cfg = map[status] || { label: status || "—", cls: "border-foreground/15 text-foreground/55" };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border ${cfg.cls}`} data-testid={`admin-temple-status-pill`}>
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}
